import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import logger from "@/lib/logger";
import { track } from "@/lib/analytics";

const TimerCtx = createContext(null);

const MODES = [
  { key: "focus", label: "FOCUS", defaultMin: 25, breakMin: 5 },
  { key: "countdown", label: "COUNTDOWN", defaultMin: 15, breakMin: 0 },
  { key: "stopwatch", label: "STOPWATCH", defaultMin: 0, breakMin: 0 },
];

const PRESETS = [10, 15, 25, 45, 60, 90];

// Bundle J — localStorage key for resuming an active timer across page reloads.
// Snapshot shape: { mode, status, seconds_at_persist, targetMin, sessionId,
// linkedBoxId, linkedTaskId, persisted_at_ms, running_since_ms? }
const SNAPSHOT_KEY = "mmntm:timer_snapshot_v1";

function formatTime(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function TimerProvider({ children }) {
  // Bundle J — Resume timer state from localStorage on mount. We persist
  // a snapshot of the active session on every meaningful state change
  // (start/pause/resume/stop/preset/mode/target) and rehydrate here so a
  // running timer survives a page refresh — previously it was wiped to
  // an idle 25:00 default. The snapshot stores `running_since_ms` (epoch)
  // for running timers so we can recompute elapsed against `Date.now()`
  // and pick up where the user left off, rather than relying on the
  // refreshed `seconds` value (which would be stale by the time the tab
  // is restored). 4-hour cap prevents a forgotten tab from auto-completing
  // a 1-day-old "running" timer.
  const initial = (() => {
    try {
      const raw = localStorage.getItem(SNAPSHOT_KEY);
      if (!raw) return null;
      const snap = JSON.parse(raw);
      if (!snap || typeof snap !== "object") return null;
      // Sanity bounds — anything older than 4 hours we treat as stale.
      if (snap.persisted_at_ms && Date.now() - snap.persisted_at_ms > 4 * 3600 * 1000) {
        return null;
      }
      return snap;
    } catch { return null; }
  })();

  const [mode, setMode] = useState(() => initial?.mode || "focus");
  const [status, setStatus] = useState(() => {
    // If we stored "running" but it's been idle past expected end, mark idle.
    if (initial?.status === "running" && initial?.running_since_ms) {
      const elapsedMs = Date.now() - initial.running_since_ms;
      const remainingSec = (initial.seconds_at_persist ?? 0) - Math.floor(elapsedMs / 1000);
      if (remainingSec <= 0 && initial.mode !== "stopwatch") return "idle";
    }
    return initial?.status === "buzzer_locked" || initial?.status === "buzzer_stoppable" || initial?.status === "break_buzzer" || initial?.status === "return_buzzer"
      ? "idle"  // Don't try to restore mid-buzzer — too noisy on reload.
      : initial?.status || "idle";
  });
  const [seconds, setSeconds] = useState(() => {
    if (!initial) return 25 * 60;
    if (initial.status === "running" && initial.running_since_ms) {
      const elapsedMs = Date.now() - initial.running_since_ms;
      if (initial.mode === "stopwatch") {
        return (initial.seconds_at_persist ?? 0) + Math.floor(elapsedMs / 1000);
      }
      return Math.max(0, (initial.seconds_at_persist ?? 0) - Math.floor(elapsedMs / 1000));
    }
    return initial.seconds_at_persist ?? (initial.targetMin || 25) * 60;
  });
  const [targetMin, setTargetMin] = useState(() => initial?.targetMin || 25);
  const [breakMin, setBreakMin] = useState(5);
  const [linkedBoxId, setLinkedBoxId] = useState(() => initial?.linkedBoxId || null);
  const [linkedTaskId, setLinkedTaskId] = useState(() => initial?.linkedTaskId || null);
  const [sessionId, setSessionId] = useState(() => initial?.sessionId || null);
  const [focusRound, setFocusRound] = useState(1);
  const [focusStats, setFocusStats] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [sessionFlash, setSessionFlash] = useState(false);
  const [boxes, setBoxes] = useState([]);
  const [lastLimitHit, setLastLimitHit] = useState(null);
  const [clockVisible, setClockVisible] = useState(() => localStorage.getItem("mmntm-clock-visible") !== "false");
  const [buzzerLockedUntil, setBuzzerLockedUntil] = useState(0);
  const [timerConfig, setTimerConfig] = useState({
    buzzer_lock_seconds: 3, default_break_minutes: 5, max_timer_minutes: 90, break_auto_start: true,
  });

  const intervalRef = useRef(null);
  const elapsedRef = useRef(initial?.elapsed || 0);
  const completeRef = useRef(null);
  const buzzerAudioRef = useRef(null);
  const returnBuzzerRef = useRef(null);

  useEffect(() => { localStorage.setItem("mmntm-clock-visible", String(clockVisible)); }, [clockVisible]);

  // Bundle J — Persist a timer snapshot whenever any field that the
  // rehydrate-on-mount logic reads back changes. We only write while the
  // user has a non-idle session (or has an in-progress sessionId) to
  // avoid bloating localStorage on every clock tick. `running_since_ms`
  // is set when status flips to "running" so reload can subtract elapsed.
  useEffect(() => {
    try {
      const isInteresting = status !== "idle" || sessionId || elapsedRef.current > 0;
      if (!isInteresting) {
        localStorage.removeItem(SNAPSHOT_KEY);
        return;
      }
      const snap = {
        mode, status, seconds_at_persist: seconds, targetMin,
        sessionId, linkedBoxId, linkedTaskId,
        elapsed: elapsedRef.current,
        persisted_at_ms: Date.now(),
        running_since_ms: status === "running" ? Date.now() : null,
      };
      localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snap));
    } catch (err) {
      // localStorage full / disabled / quota — no point retrying every
      // tick. Log so reload-resume regressions are debuggable.
      logger.warn("timer:snapshot persist failed", err);
    }
  }, [mode, status, targetMin, sessionId, linkedBoxId, linkedTaskId]);
  // Note: `seconds` intentionally omitted from the dep list — we don't want
  // to thrash localStorage every second. The interval-driven elapsed is
  // recovered by `running_since_ms` on next mount instead.

  useEffect(() => {
    (async () => {
      try {
        const cfg = await api.get("/timer/config");
        if (cfg) { setTimerConfig(cfg); setBreakMin(cfg.default_break_minutes || 5); }
      } catch (e) { logger.error("timerConfig:", e); /* use defaults */ }
    })();
  }, []);

  const playBuzzer = useCallback(() => {
    try {
      if (!buzzerAudioRef.current) buzzerAudioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = buzzerAudioRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "square";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(660, ctx.currentTime + 0.15);
      osc.frequency.setValueAtTime(880, ctx.currentTime + 0.3);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.5);
    } catch (e) { logger.error("buzzer:", e); }
  }, []);

  const stopBuzzerLoop = useCallback(() => {
    if (returnBuzzerRef.current) { clearInterval(returnBuzzerRef.current); returnBuzzerRef.current = null; }
  }, []);

  const fetchBoxes = useCallback(async () => {
    try { setBoxes(await api.getBoxes()); } catch (e) { logger.error("fetchBoxes:", e); }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const [stats, recent] = await Promise.all([api.getFocusStats(), api.getFocusSessions({ limit: 10 })]);
      setFocusStats(stats); setRecentSessions(recent); setCompletedCount(stats.total_sessions || 0);
    } catch (e) { logger.error("fetchStats:", e); }
  }, []);

  useEffect(() => { fetchBoxes(); fetchStats(); }, [fetchBoxes, fetchStats]);
  useEffect(() => { const id = setInterval(fetchBoxes, 30000); return () => clearInterval(id); }, [fetchBoxes]);

  // --- Stabilize values used in callbacks via refs ---
  const stateRef = useRef({ status, mode, targetMin, breakMin, sessionId, timerConfig });
  stateRef.current = { status, mode, targetMin, breakMin, sessionId, timerConfig };

  completeRef.current = async () => {
    const { status: st, sessionId: sid, timerConfig: cfg } = stateRef.current;
    if (st === "break") {
      playBuzzer(); setStatus("break_buzzer");
      returnBuzzerRef.current = setInterval(playBuzzer, (cfg.buzzer_lock_seconds || 3) * 1000 + 5000);
      return;
    }
    if (sid) {
      try { await api.updateFocusSession(sid, { status: "completed", actual_seconds: elapsedRef.current }); track("timer_completed", { elapsed: elapsedRef.current, mode: stateRef.current.mode }); }
      catch (e) { logger.error("completeSession:", e); }
    }
    setCompletedCount((p) => p + 1); setSessionId(null);
    setSessionFlash(true); setTimeout(() => setSessionFlash(false), 1500);
    playBuzzer();
    const lockMs = (cfg.buzzer_lock_seconds || 3) * 1000;
    setBuzzerLockedUntil(Date.now() + lockMs); setStatus("buzzer_locked");
    setTimeout(() => { setStatus((prev) => prev === "buzzer_locked" ? "buzzer_stoppable" : prev); }, lockMs);
    fetchStats(); fetchBoxes();
  };

  useEffect(() => {
    if (status === "running" || status === "break") {
      intervalRef.current = setInterval(() => {
        setSeconds((prev) => {
          if (stateRef.current.mode === "stopwatch") { elapsedRef.current += 1; return prev + 1; }
          if (prev <= 1) { clearInterval(intervalRef.current); completeRef.current?.(); return 0; }
          elapsedRef.current += 1; return prev - 1;
        });
      }, 1000);
    } else { clearInterval(intervalRef.current); }
    return () => clearInterval(intervalRef.current);
  }, [status]);

  // --- Actions: use stateRef to keep dep arrays small ---
  const dismissBuzzer = useCallback(() => {
    const { status: st, mode: m, targetMin: tm, breakMin: bm, timerConfig: cfg } = stateRef.current;
    if (st !== "buzzer_stoppable") return;
    stopBuzzerLoop();
    if (m === "focus" && cfg.break_auto_start && bm > 0) {
      setStatus("break"); setFocusRound((p) => p + 1); setSeconds(bm * 60); elapsedRef.current = 0;
      toast.success("Break started!");
    } else {
      setStatus("idle"); setSeconds(tm * 60); elapsedRef.current = 0;
      toast.success("Session complete!");
    }
  }, [stopBuzzerLoop]);

  const dismissBreakBuzzer = useCallback(() => {
    stopBuzzerLoop(); setStatus("idle"); setSeconds(stateRef.current.targetMin * 60);
    elapsedRef.current = 0; toast.success("Break complete! Ready to focus.");
  }, [stopBuzzerLoop]);

  const handleStart = useCallback(async () => {
    const { status: st, mode: m, targetMin: tm } = stateRef.current;
    if (st === "idle" || st === "return_buzzer") {
      stopBuzzerLoop(); elapsedRef.current = 0; setSeconds(m !== "stopwatch" ? tm * 60 : 0);
      try {
        const s = await api.createFocusSession({ mode: m === "focus" ? "focus" : m, box_id: stateRef.current.linkedBoxId, task_id: stateRef.current.linkedTaskId, target_minutes: tm });
        setSessionId(s.id);
        track("timer_started", { duration_planned: m === "stopwatch" ? 0 : tm * 60, mode: m });
        if (s._daily_limit?.reached) setLastLimitHit(s._daily_limit);
      } catch (e) { logger.error("createFocusSession:", e); }
    }
    setStatus("running");
  }, [stopBuzzerLoop]);

  const handlePause = useCallback(() => { setStatus("paused"); }, []);
  const handleResume = useCallback(() => { setStatus("running"); }, []);

  const handleStop = useCallback(async () => {
    const { sessionId: sid, mode: m, targetMin: tm } = stateRef.current;
    stopBuzzerLoop();
    if (sid && elapsedRef.current > 0) {
      try {
        await api.updateFocusSession(sid, { status: "completed", actual_seconds: elapsedRef.current });
        track("timer_completed", { elapsed: elapsedRef.current, mode: m });
        setCompletedCount((p) => p + 1); setSessionFlash(true); setTimeout(() => setSessionFlash(false), 1500);
        fetchStats(); fetchBoxes();
      } catch (e) { logger.error("stopSession:", e); }
    }
    setStatus("idle"); setSessionId(null); elapsedRef.current = 0;
    setSeconds(m === "stopwatch" ? 0 : tm * 60); setFocusRound(1);
  }, [stopBuzzerLoop, fetchStats, fetchBoxes]);

  const handleReset = useCallback(() => {
    const { sessionId: sid, mode: m, targetMin: tm } = stateRef.current;
    stopBuzzerLoop();
    if (sid) {
      const elapsed = elapsedRef.current;
      api.updateFocusSession(sid, { status: "cancelled", actual_seconds: 0 }).then(() => track("timer_abandoned", { elapsed, mode: m })).catch((e) => logger.error(e));
    }
    setStatus("idle"); setSessionId(null); elapsedRef.current = 0;
    setSeconds(m === "stopwatch" ? 0 : tm * 60); setFocusRound(1);
  }, [stopBuzzerLoop]);

  const switchMode = useCallback((m) => {
    if (stateRef.current.status !== "idle") return;
    setMode(m);
    const conf = MODES.find((x) => x.key === m);
    setTargetMin(conf.defaultMin); setSeconds(m === "stopwatch" ? 0 : conf.defaultMin * 60); elapsedRef.current = 0;
  }, []);

  const setPreset = useCallback((min) => {
    const { status: st, mode: m, timerConfig: cfg } = stateRef.current;
    if (st !== "idle" || m === "stopwatch") return;
    const clamped = Math.max(1, Math.min(cfg.max_timer_minutes || 90, min));
    setTargetMin(clamped); setSeconds(clamped * 60);
  }, []);

  const adjustTime = useCallback((d) => {
    const { status: st, mode: m, targetMin: tm, timerConfig: cfg } = stateRef.current;
    if (st !== "idle" || m === "stopwatch") return;
    const n = Math.max(1, Math.min(cfg.max_timer_minutes || 90, tm + d));
    setTargetMin(n); setSeconds(n * 60);
  }, []);

  const linkBox = useCallback((id) => { setLinkedBoxId(id); setLinkedTaskId(null); }, []);
  const linkTask = useCallback((id) => { setLinkedTaskId(id); }, []);
  const toggleClock = useCallback(() => setClockVisible((p) => !p), []);

  useEffect(() => { return () => { stopBuzzerLoop(); clearInterval(intervalRef.current); }; }, [stopBuzzerLoop]);

  // --- Split the value into focused sub-memos (≤5 deps each) ---
  const timerCore = useMemo(() => ({
    mode, status, seconds,
  }), [mode, status, seconds]);

  const timerConfig_ = useMemo(() => ({
    targetMin, breakMin,
  }), [targetMin, breakMin]);

  const linkState = useMemo(() => ({
    linkedBoxId, linkedTaskId, sessionId, focusRound,
  }), [linkedBoxId, linkedTaskId, sessionId, focusRound]);

  const dataState = useMemo(() => ({
    focusStats, recentSessions, completedCount, boxes,
  }), [focusStats, recentSessions, completedCount, boxes]);

  const uiFlags = useMemo(() => ({
    sessionFlash, lastLimitHit, clockVisible,
  }), [sessionFlash, lastLimitHit, clockVisible]);

  const uiBuzzer = useMemo(() => ({
    buzzerLockedUntil, timerConfig,
  }), [buzzerLockedUntil, timerConfig]);

  const derived = useMemo(() => {
    const totalSec = mode === "stopwatch" ? 1 : targetMin * 60;
    return {
      progress: mode === "stopwatch" ? Math.min(100, (seconds / 3600) * 100) : ((totalSec - seconds) / totalSec) * 100,
      isActive: status === "running" || status === "paused" || status === "break",
      isBuzzing: status === "buzzer_locked" || status === "buzzer_stoppable" || status === "break_buzzer" || status === "return_buzzer",
      displayTime: formatTime(seconds),
      modeConfig: MODES.find((m) => m.key === mode),
    };
  }, [mode, status, seconds, targetMin]);

  const actions = useMemo(() => ({
    start: handleStart, pause: handlePause, resume: handleResume,
    stop: handleStop, reset: handleReset,
  }), [handleStart, handlePause, handleResume, handleStop, handleReset]);

  const timeControls = useMemo(() => ({
    switchMode, adjustTime, setPreset, setBreakMin,
  }), [switchMode, adjustTime, setPreset]);

  const linkControls = useMemo(() => ({
    linkBox, linkTask, toggleClock,
  }), [linkBox, linkTask, toggleClock]);

  const buzzerActions = useMemo(() => ({
    dismissBuzzer, dismissBreakBuzzer, fetchStats, fetchBoxes,
    clearLimitHit: () => setLastLimitHit(null),
  }), [dismissBuzzer, dismissBreakBuzzer, fetchStats, fetchBoxes]);

  const timerStateGroup = useMemo(() => ({
    ...timerCore, ...timerConfig_, ...linkState, ...dataState,
  }), [timerCore, timerConfig_, linkState, dataState]);

  const timerUIGroup = useMemo(() => ({
    ...uiFlags, ...uiBuzzer, ...derived,
  }), [uiFlags, uiBuzzer, derived]);

  const timerActionGroup = useMemo(() => ({
    ...actions, ...timeControls, ...linkControls, ...buzzerActions,
  }), [actions, timeControls, linkControls, buzzerActions]);

  const value = useMemo(() => ({
    ...timerStateGroup, ...timerUIGroup, ...timerActionGroup,
    MODES, PRESETS,
  }), [timerStateGroup, timerUIGroup, timerActionGroup]);

  return <TimerCtx.Provider value={value}>{children}</TimerCtx.Provider>;
}

export function useTimer() {
  const ctx = useContext(TimerCtx);
  if (!ctx) throw new Error("useTimer must be used within TimerProvider");
  return ctx;
}

export { MODES, PRESETS, formatTime };
